import type { MetaDescriptor } from 'react-router';

type GetMatches<T> = T extends { id: string } ? T : never;
type GetMatch<M extends { id: string }, I extends M['id']> = M extends { id: I }
  ? M
  : never;

export function findMetaMatch<T, M extends GetMatches<T>, I extends M['id']>(
  id: I,
  matches: T[],
): GetMatch<M, I> {
  return matches.find((match) => (match as any).id === id) as any;
}

export class MetaStack {
  private stack: MetaDescriptor[];

  constructor(stack: MetaDescriptor[] = []) {
    this.stack = stack;
  }

  isOverridableMeta(descriptor: MetaDescriptor) {
    if ('charSet' in descriptor) return true;
    if ('title' in descriptor) return true;
    if ('name' in descriptor) return true;
    if ('property' in descriptor) return descriptor.property === 'og:image';

    return false;
  }

  getUniqueKey(descriptor: MetaDescriptor): string {
    // We use the 'in' operator for type narrowing to correctly identify the descriptor's type.
    if ('charSet' in descriptor) {
      return 'charSet';
    }
    if ('title' in descriptor) {
      return 'title';
    }
    if ('name' in descriptor) {
      return `name:${descriptor.name}`;
    }
    if ('property' in descriptor) {
      return `property:${descriptor.property}`;
    }
    if ('httpEquiv' in descriptor) {
      return `httpEquiv:${descriptor.httpEquiv}`;
    }
    if ('script:ld+json' in descriptor) {
      return 'script:ld+json';
    }
    // As a fallback, we stringify it to ensure a unique key.
    return JSON.stringify(descriptor);
  }

  push(meta: MetaDescriptor) {
    const isOverridable = this.isOverridableMeta(meta);
    if (!isOverridable) {
      this.stack.push(meta);
      return;
    }

    const filteredItems = this.stack.filter((item) => {
      const itemKey = this.getUniqueKey(item);
      const metaKey = this.getUniqueKey(meta);
      return itemKey !== metaKey;
    });

    filteredItems.unshift(meta);
    this.stack = filteredItems;
  }

  title({ title, brandName }: { title?: string | null; brandName?: string }) {
    if (!title) return;
    this.push({ title: `${title} | ${brandName}` });
  }

  description(description?: string | null) {
    if (!description) return;
    this.push({ name: 'description', content: description });
  }

  get() {
    return this.stack;
  }
}
